import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Section, StatBadge } from "@/components/Chrome";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TelegramLogo, WhatsappLogo } from "@phosphor-icons/react";

export default function Setup() {
  const qc = useQueryClient();
  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: api.profile.get });
  const [form, setForm] = useState({});

  useEffect(() => { if (profile) setForm(profile); }, [profile]);

  const save = useMutation({
    mutationFn: (data) => api.profile.update(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["profile"] }); toast.success("Saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || e.message),
  });

  const setWebhook = useMutation({
    mutationFn: () => api.telegram.setWebhook(),
    onSuccess: (d) => toast.success(`Webhook registered: ${d.webhook_url}`),
    onError: (e) => toast.error(e.response?.data?.detail || e.message),
  });

  if (!profile) return <div className="p-10 text-sm text-stone-500">Loading…</div>;

  return (
    <div data-testid="setup-page">
      <PageHeader eyebrow="Onboarding" title="Setup" subtitle="Configure who Copilot is working for, what timezone it lives in, and how it reaches you." />

      <Section title="Principal">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          <Field label="Display name" testid="field-display-name" value={form.name || ""} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Timezone" testid="field-timezone" value={form.timezone || ""} onChange={(v) => setForm({ ...form, timezone: v })} placeholder="e.g. America/New_York" />
          <Field label="Daily briefing (HH:MM)" testid="field-briefing" value={form.briefing_time || ""} onChange={(v) => setForm({ ...form, briefing_time: v })} placeholder="07:00" />
        </div>
        <button data-testid="save-profile"
          onClick={() => save.mutate({ name: form.name, timezone: form.timezone, briefing_time: form.briefing_time })}
          className="mt-6 bg-orange-600 hover:bg-orange-500 text-white text-xs uppercase tracking-widest font-mono px-4 py-2 transition-colors">
          Save preferences
        </button>
      </Section>

      <Section title="Messaging channels">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          <div className="border border-stone-800 p-6" data-testid="channel-telegram">
            <div className="flex items-center gap-3 mb-3">
              <TelegramLogo size={22} weight="duotone" className="text-sky-400" />
              <h3 className="text-lg text-stone-100">Telegram</h3>
              <StatBadge status={profile.telegram_bot_username ? "success" : "info"}>
                {profile.telegram_bot_username ? "Configured" : "Not configured"}
              </StatBadge>
            </div>
            <p className="text-xs text-stone-500 mb-4">
              Create a bot with @BotFather, paste the token below, then click &ldquo;Register webhook&rdquo;.
            </p>
            <input
              data-testid="tg-token"
              placeholder="123456:ABC-DEF..."
              value={form.telegram_bot_token || ""}
              onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })}
              className="w-full bg-stone-900 border border-stone-800 px-3 py-2 text-sm text-stone-100 font-mono focus:border-orange-600 outline-none"
            />
            <div className="flex gap-2 mt-3">
              <button data-testid="tg-save"
                onClick={() => save.mutate({ telegram_bot_token: form.telegram_bot_token })}
                className="text-[10px] uppercase tracking-widest font-mono px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white transition-colors">
                Save token
              </button>
              <button data-testid="tg-webhook"
                onClick={() => setWebhook.mutate()}
                disabled={!profile.telegram_bot_token}
                className="text-[10px] uppercase tracking-widest font-mono px-3 py-1.5 border border-stone-700 text-stone-200 hover:border-stone-500 transition-colors disabled:opacity-40">
                Register webhook
              </button>
            </div>
            {profile.telegram_bot_username && (
              <div className="mt-4 text-xs font-mono text-stone-400">
                Bot: <a href={`https://t.me/${profile.telegram_bot_username}`} target="_blank" rel="noreferrer" className="text-orange-500 hover:underline">@{profile.telegram_bot_username}</a>
                {profile.telegram_chat_id && <> · Chat: {profile.telegram_chat_id}</>}
              </div>
            )}
          </div>

          <div className="border border-stone-800 p-6" data-testid="channel-whatsapp">
            <div className="flex items-center gap-3 mb-3">
              <WhatsappLogo size={22} weight="duotone" className="text-green-500" />
              <h3 className="text-lg text-stone-100">WhatsApp</h3>
              <StatBadge status="warning">Phase 2</StatBadge>
            </div>
            <p className="text-xs text-stone-500">
              Twilio WhatsApp integration is wired in the roadmap — provide Twilio SID + Auth Token + WhatsApp sender number to enable. Deferred to phase 2 per your choice.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-stone-900 border border-stone-800 px-3 py-2 text-sm text-stone-100 focus:border-orange-600 outline-none"
      />
    </label>
  );
}
